import { Button } from '@components/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@components/form/form';
import { Input } from '@components/form/input';
import { useStaffUserUpdateForm } from '@hooks/staff/users/editForm';
import { StaffUser } from '@root/globalTypes';
import { useGetPermissionGroups } from '@services/staff/users/permissionGroups';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { PermissionGroups } from '@components/permissionGroups';

export function EditStaffUserForm({ user }: { user: StaffUser }) {
  const { t } = useTranslation('app');
  const { localId } = useParams({ strict: false });
  const navigate = useNavigate();

  const { data: permissions } = useGetPermissionGroups();
  const { form, onSubmit, error, isPending } = useStaffUserUpdateForm({
    user,
    successHandler: () => {
      form.reset();
      toast.success(t('toast.users.updateSuccess'));
      navigate({
        to: '/u/$localId/staff/users/$id',
        params: { id: user.id, localId: localId || '0' },
        replace: true,
      }).catch((err) => {
        console.error(err);
      });
    },
  });

  if (!permissions) return null;

  return (
    <Form {...form}>
      <form
        id="userForm"
        className="flex flex-col gap-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>{t('form.name.label')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormLabel className="static ml-3 mt-2">{t('form.permissions.label')}</FormLabel>
        <FormField
          control={form.control}
          name="permissions"
          render={({ field }) => (
            <PermissionGroups
              permissions={permissions}
              selectedValues={field.value}
              onSelect={(perms: string[]) => form.setValue('permissions', perms)}
            />
          )}
        />
        {error && <p className="text-red">{error}</p>}

        <Button type="submit" variant="primary" disabled={isPending} loading={isPending}>
          {t('form.submit.userUpdate')}
        </Button>
      </form>
    </Form>
  );
}
